import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { H1 } from "./AdminShared";

const cls = "w-full rounded-xl border-2 border-[#E2EBF0] px-3 py-2 text-sm focus:border-[#008057] outline-none";
const box = "rounded-2xl bg-white border border-[#E2EBF0] p-6";
const numCls = "w-28 rounded-xl border-2 border-[#E2EBF0] px-3 py-1.5 text-sm text-right";

const parseSizes = (sizes) => Object.fromEntries(Object.entries(sizes).map(([k, v]) => [k, Array.isArray(v) ? v : String(v).split(",").map((s) => s.trim()).filter(Boolean)]));
const toNumOrNull = (v) => (v ? Number(v) : null);

function PricesCard({ prices, setPrices }) {
  return (
    <div className={box}>
      <div className="font-bold text-[#03281B] mb-4">Base Registration Prices (USD)</div>
      {["early_bird", "general", "late_bloomer"].map((k) => (
        <div key={k} className="flex items-center justify-between mb-3"><span className="text-sm capitalize">{k.replace("_", " ")}</span>
          <input data-testid={`config-price-${k}`} type="number" className={numCls} value={prices[k]} onChange={(e) => setPrices({ ...prices, [k]: e.target.value })} /></div>
      ))}
    </div>
  );
}

function SponsorPricesCard({ spPkgs, setSpPkgs }) {
  return (
    <div className={box}>
      <div className="font-bold text-[#03281B] mb-4">Sponsorship Packages (USD, package total)</div>
      {spPkgs.map((p) => (
        <div key={p.key} className="flex items-center justify-between mb-3"><span className="text-sm">{p.name} ({p.children} child{p.children > 1 ? "ren" : ""})</span>
          <input data-testid={`config-sponsor-price-${p.key}`} type="number" className={numCls} value={p.price} onChange={(e) => setSpPkgs(spPkgs.map((x) => (x.key === p.key ? { ...x, price: e.target.value } : x)))} /></div>
      ))}
    </div>
  );
}

function SizesCard({ sizes, setSizes }) {
  return (
    <div className={`${box} lg:col-span-2`}>
      <div className="font-bold text-[#03281B] mb-1">Clothing Sizes</div>
      <p className="text-xs text-slate-500 mb-4">Comma-separated. These are configurable placeholders, not an official Sporting CP chart.</p>
      {["tshirt", "shorts", "socks", "jersey"].map((k) => (
        <label key={k} className="block mb-3"><span className="text-sm capitalize">{k}</span>
          <input data-testid={`config-sizes-${k}`} className={cls} value={Array.isArray(sizes[k]) ? sizes[k].join(", ") : sizes[k]} onChange={(e) => setSizes({ ...sizes, [k]: e.target.value })} /></label>
      ))}
    </div>
  );
}

function CapacityCard({ capacity, setCapacity }) {
  return (
    <div className={`${box} lg:col-span-2`}>
      <div className="font-bold text-[#03281B] mb-4">Session Capacity & Scheduling</div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className="text-sm">Soccer sessions per day (blank = unlimited)</span><input className={cls} value={capacity.football_per_day ?? ""} onChange={(e) => setCapacity({ ...capacity, football_per_day: toNumOrNull(e.target.value) })} /></label>
        <label className="block"><span className="text-sm">Goalkeeper sessions per day (blank = unlimited)</span><input className={cls} value={capacity.goalkeeper_per_day ?? ""} onChange={(e) => setCapacity({ ...capacity, goalkeeper_per_day: toNumOrNull(e.target.value) })} /></label>
      </div>
      <label className="block mt-4"><span className="text-sm">Scheduling notes</span><input className={cls} value={capacity.notes || ""} onChange={(e) => setCapacity({ ...capacity, notes: e.target.value })} /></label>
    </div>
  );
}

export function ConfigEditor({ config, onSaved }) {
  const [prices, setPrices] = useState(config.base_prices);
  const [sizes, setSizes] = useState(config.clothing_sizes);
  const [deadline, setDeadline] = useState(config.bank_transfer_deadline_days);
  const [giftTotal, setGiftTotal] = useState(config.gift_total);
  const [capacity, setCapacity] = useState(config.session_capacity || {});
  const [spPkgs, setSpPkgs] = useState(config.sponsor_packages || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/config", {
        base_prices: { early_bird: Number(prices.early_bird), general: Number(prices.general), late_bloomer: Number(prices.late_bloomer) },
        clothing_sizes: parseSizes(sizes),
        bank_transfer_deadline_days: Number(deadline),
        gift_total: Number(giftTotal),
        session_capacity: capacity,
        sponsor_packages: spPkgs.map((p) => ({ key: p.key, price: Number(p.price) })),
      });
      onSaved(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <H1>Configuration</H1>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <PricesCard prices={prices} setPrices={setPrices} />
        <SponsorPricesCard spPkgs={spPkgs} setSpPkgs={setSpPkgs} />
        <div className={box}>
          <div className="font-bold text-[#03281B] mb-4">Deadlines & Promotion</div>
          <label className="block mb-3"><span className="text-sm">Bank transfer payment deadline (days)</span><input data-testid="config-deadline-days" type="number" className={cls} value={deadline} onChange={(e) => setDeadline(e.target.value)} /></label>
          <label className="block"><span className="text-sm">Total promotional jerseys</span><input data-testid="config-gift-total" type="number" className={cls} value={giftTotal} onChange={(e) => setGiftTotal(e.target.value)} /></label>
        </div>
        <SizesCard sizes={sizes} setSizes={setSizes} />
        <CapacityCard capacity={capacity} setCapacity={setCapacity} />
      </div>
      <button data-testid="config-save" onClick={save} disabled={saving} className="mt-6 rounded-full bg-[#008057] text-white px-8 py-3 font-bold disabled:opacity-60">{saving ? "Saving..." : "Save Configuration"}</button>
    </div>
  );
}
